<template>
  <v-text-field
    :disabled="field.readonly"
    :error-messages="errors"
    :hide-details="dense"
    :model-value="modelValue"
    :rules="rules"
    :type="field.type"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #label>
      <FieldLabel :field="field" />
    </template>

    <template
      v-if="field.icon"
      #prepend-inner
    >
      <vi-icon
        :icon="field.icon"
      />
    </template>

    <template
      #append-inner
    >
      <icon-button
        :disabled="!canStepDown(field)"
        icon="mdi:minus"
        icon-size="24"
        ripple
        size="36"
        variant="flat"
        @click="stepValue(field, 'down')"
      />

      <icon-button
        :disabled="!canStepUp(field)"
        icon="mdi:plus"
        icon-size="24"
        ripple
        size="36"
        variant="flat"
        @click="stepValue(field, 'up')"
      />
    </template>
  </v-text-field>
</template>

<script setup lang="ts">
const props = defineProps<{
  field: FieldConfig
  rules?: FormRule[]
  errors?: string[]
  dense?: boolean
  modelValue: number | string | undefined
}>();
const $emit = defineEmits(['update:modelValue']);

/**
 * Gets what the value would be if the `modelValue` were stepped in the given direction
 *
 * @param field
 * @param direction The direction the value should be changed in
 */
const getSteppedValue = (field: FieldConfig, direction: 'up' | 'down') => {
  const directionInt = direction === 'down' ? -1 : 1;
  const step = (field.step ?? 1) * directionInt;
  return Number.parseFloat(props.modelValue?.toString() || '0') + step;
};

/**
 * Determines if the value can be stepped up without exceeding the `maxValue`, if any
 *
 * @param field
 */
const canStepUp = (field: FieldConfig) => {
  return typeof field.maxValue === 'undefined' || getSteppedValue(field, 'up') <= field.maxValue;
};

/**
 * Determines if the value can be stepped down without subceeding the `minValue`, if any
 *
 * @param field
 */
const canStepDown = (field: FieldConfig) => {
  return typeof field.minValue === 'undefined' || getSteppedValue(field, 'down') >= field.minValue;
};

/**
 * If the value can be safely stepped in the given direction, updates the `modelValue` a step.
 * @param field
 * @param direction
 */
const stepValue = (field: FieldConfig, direction: 'up' | 'down') => {
  if ((direction === 'up' && canStepUp(field)) || (direction === 'down' && canStepDown(field))) {
    $emit('update:modelValue', getSteppedValue(field, direction));
  }
};
</script>
